import { pageWrap, tableHeaderCell, tableRow, btnPrimary, minutesFmt } from "../../utils/pageStyles";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Drawer,
  Stack,
  TextField,
  Typography,
  Chip,
  FormControlLabel,
  Switch,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Tooltip,
  IconButton,
  Alert,
  MenuItem,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import RefreshIcon from "@mui/icons-material/Refresh";
import SaveIcon from "@mui/icons-material/Save";
import TuneIcon from "@mui/icons-material/Tune";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";

import { AdminPackagesAPI } from "../../api/adminPackagesApi";
import PackScopeEditor from "../../components/admin/PackScopeEditor";

const emptyForm = {
  code: "",
  name: "",
  description: "",
  total_minutes: 0,
  validity_days: null,
  is_active: true,
};

export default function AdminPackagesPage() {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);

  const [search, setSearch] = useState("");
  const [isActive, setIsActive] = useState(""); // "", "true", "false"

  // drawer (create/edit)
  const [openDrawer, setOpenDrawer] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });

  // delete dialog
  const [deleteTarget, setDeleteTarget] = useState(null);

  // scope editor (advanced)
  const [scopeOpen, setScopeOpen] = useState(false);
  const [scopePack, setScopePack] = useState(null);

  // Fetch unique page (page_size=100) puis pagination client (DataGrid)
  const params = useMemo(() => {
    const p = { page: 1, page_size: 100 };
    if (search.trim()) p.search = search.trim();
    if (isActive !== "") p.is_active = isActive; // backend attend "true"/"false" ou bool; ton helper stringify
    return p;
  }, [search, isActive]);

  const fetchPacks = useCallback(async () => {
    setLoading(true);
    try {
      const data = await AdminPackagesAPI.listPacks(params);
      setRows(data?.items || []);
      setTotal(data?.total || 0);
    } catch (e) {
      enqueueSnackbar(t("snackbar.error"), { variant: "error" });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar, params, t]);

  useEffect(() => {
    fetchPacks();
  }, [fetchPacks]);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyForm });
    setOpenDrawer(true);
  };

  const openEdit = (pack) => {
    setEditing(pack);
    setForm({
      code: pack.code || "",
      name: pack.name || "",
      description: pack.description || "",
      total_minutes: pack.total_minutes || 0,
      validity_days: pack.validity_days ?? null,
      is_active: !!pack.is_active,
    });
    setOpenDrawer(true);
  };

  const openScopeEditor = (pack) => {
    setScopePack(pack);
    setScopeOpen(true);
  };

  const handleDuplicate = async (pack) => {
    setLoading(true);
    try {
      const created = await AdminPackagesAPI.duplicatePack(pack);
      enqueueSnackbar(
        t("admin.packages.snack.duplicated", { code: created?.code || `${pack.code}_COPY` }),
        { variant: "success" }
      );
      await fetchPacks();
    } catch (e) {
      enqueueSnackbar(t("snackbar.error"), { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  const savePack = async () => {
    setLoading(true);
    try {
      if (!form.code.trim() || !form.name.trim()) {
        enqueueSnackbar(t("admin.packages.snack.missingFields"), { variant: "warning" });
        return;
      }

      const payload = {
        ...form,
        code: form.code.trim(),
        name: form.name.trim(),
        description: form.description?.trim() || null,
        total_minutes: Number(form.total_minutes || 0),
        validity_days:
          form.validity_days === "" || form.validity_days === null ? null : Number(form.validity_days),
        is_active: !!form.is_active,
      };

      if (editing?.id) {
        await AdminPackagesAPI.updatePack(editing.id, payload);
        enqueueSnackbar(t("admin.packages.snack.updated", { code: payload.code }), { variant: "success" });
      } else {
        await AdminPackagesAPI.createPack(payload);
        enqueueSnackbar(t("admin.packages.snack.created", { code: payload.code }), { variant: "success" });
      }

      await fetchPacks();
      setOpenDrawer(false);
    } catch (e) {
      enqueueSnackbar(t("snackbar.error"), { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  const confirmDelete = (pack) => setDeleteTarget(pack);

  const doDelete = async () => {
    if (!deleteTarget?.id) return;
    setLoading(true);
    try {
      await AdminPackagesAPI.deletePack(deleteTarget.id);
      enqueueSnackbar(t("admin.packages.snack.deleted", { code: deleteTarget.code }), { variant: "success" });
      setDeleteTarget(null);
      await fetchPacks();
      if (editing?.id === deleteTarget.id) setOpenDrawer(false);
    } catch (e) {
      enqueueSnackbar(t("snackbar.error"), { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  const columns = useMemo(
    () => [
      { field: "id", headerName: "ID", width: 70 },
      { field: "code", headerName: t("admin.packages.columns.code"), width: 130 },
      { field: "name", headerName: t("admin.packages.columns.name"), flex: 1, minWidth: 200 },
      { field: "total_minutes", headerName: t("admin.packages.columns.minutes"), width: 140 },
      {
        field: "is_active",
        headerName: t("admin.packages.columns.active"),
        width: 120,
        renderCell: (params) =>
          params.value ? (
            <Chip size="small" label={t("admin.packages.active")} />
          ) : (
            <Chip size="small" variant="outlined" label={t("admin.packages.inactive")} />
          ),
      },
      {
        field: "actions",
        headerName: t("common.actions"),
        width: 210,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const pack = params.row;
          return (
            <Stack direction="row" spacing={0.5}>
              <Tooltip title={t("admin.packages.scope.edit")}>
                <IconButton size="small" onClick={() => openScopeEditor(pack)}>
                  <TuneIcon fontSize="inherit" />
                </IconButton>
              </Tooltip>

              <Tooltip title={t("common.edit")}>
                <IconButton size="small" onClick={() => openEdit(pack)}>
                  <EditIcon fontSize="inherit" />
                </IconButton>
              </Tooltip>

              <Tooltip title={t("common.duplicate")}>
                <IconButton size="small" onClick={() => handleDuplicate(pack)}>
                  <ContentCopyIcon fontSize="inherit" />
                </IconButton>
              </Tooltip>

              <Tooltip title={t("common.delete")}>
                <IconButton size="small" onClick={() => confirmDelete(pack)}>
                  <DeleteIcon fontSize="inherit" />
                </IconButton>
              </Tooltip>
            </Stack>
          );
        },
      },
    ],
    [t]
  );

  return (
    <Box sx={pageWrap}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h5">{t("admin.packages.title")}</Typography>
          <Typography variant="body2" color="text.secondary">
            {t("admin.packages.subtitle")}
          </Typography>
        </Box>

        <Stack direction="row" spacing={1}>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchPacks} disabled={loading}>
            {t("common.refresh")}
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate} disabled={loading}>
            {t("common.add")}
          </Button>
        </Stack>
      </Stack>

      <Card>
        <CardContent>
          <Stack direction={{ xs: "column", md: "row" }} spacing={1} sx={{ mb: 1 }}>
            <TextField
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              label={t("common.search")}
              placeholder={t("admin.packages.searchPlaceholder")}
              size="small"
              sx={{ width: { xs: "100%", md: 320 } }}
            />
            <TextField
              select
              value={isActive}
              onChange={(e) => setIsActive(e.target.value)}
              label={t("admin.packages.filters.active")}
              size="small"
              sx={{ width: { xs: "100%", md: 200 } }}
            >
              <MenuItem value="">{t("admin.packages.filters.all")}</MenuItem>
              <MenuItem value="true">{t("admin.packages.filters.onlyActive")}</MenuItem>
              <MenuItem value="false">{t("admin.packages.filters.onlyInactive")}</MenuItem>
            </TextField>

            <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 1, color: "text.secondary" }}>
              {loading ? <CircularProgress size={18} /> : null}
              <Typography variant="body2">{t("admin.packages.count", { total })}</Typography>
            </Box>
          </Stack>

          <Box sx={{ height: 520 }}>
            <DataGrid
              rows={rows}
              columns={columns}
              getRowId={(r) => r.id}
              loading={loading}
              disableRowSelectionOnClick
              pageSizeOptions={[10, 25, 50, 100]}
              initialState={{
                pagination: { paginationModel: { pageSize: 25, page: 0 } },
              }}
            />
          </Box>

          <Alert severity="info" sx={{ mt: 2 }}>
            {t("admin.packages.lazyHint")}
          </Alert>
        </CardContent>
      </Card>

      {/* Drawer editor (pack only) */}
      <Drawer anchor="right" open={openDrawer} onClose={() => setOpenDrawer(false)}>
        <Box sx={{ width: { xs: 360, md: 460 }, p: 2 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            {editing
              ? t("admin.packages.drawer.editTitle", { id: editing.id })
              : t("admin.packages.drawer.createTitle")}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t("admin.packages.drawer.help")}
          </Typography>

          <Stack spacing={1.5}>
            <TextField
              label={t("admin.packages.fields.code")}
              value={form.code}
              onChange={(e) => setForm((s) => ({ ...s, code: e.target.value }))}
              disabled={!!editing}
            />
            <TextField
              label={t("admin.packages.fields.name")}
              value={form.name}
              onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))}
            />
            <TextField
              label={t("admin.packages.fields.description")}
              value={form.description}
              onChange={(e) => setForm((s) => ({ ...s, description: e.target.value }))}
              multiline
              minRows={3}
            />

            <Stack direction="row" spacing={1}>
              <TextField
                label={t("admin.packages.fields.totalMinutes")}
                type="number"
                value={form.total_minutes}
                onChange={(e) => setForm((s) => ({ ...s, total_minutes: e.target.value }))}
                fullWidth
              />
              <TextField
                label={t("admin.packages.fields.validityDays")}
                type="number"
                value={form.validity_days ?? ""}
                onChange={(e) => setForm((s) => ({ ...s, validity_days: e.target.value }))}
                fullWidth
              />
            </Stack>

            <FormControlLabel
              control={
                <Switch
                  checked={!!form.is_active}
                  onChange={(e) => setForm((s) => ({ ...s, is_active: e.target.checked }))}
                />
              }
              label={t("admin.packages.fields.active")}
            />

            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                startIcon={loading ? <CircularProgress size={18} /> : <SaveIcon />}
                onClick={savePack}
                disabled={loading}
                fullWidth
              >
                {t("common.save")}
              </Button>
              <Button variant="outlined" onClick={() => setOpenDrawer(false)} disabled={loading} fullWidth>
                {t("common.cancel")}
              </Button>
            </Stack>

            <Divider sx={{ my: 1 }} />

            {editing?.id ? (
              <Alert severity="info">
                {t("admin.packages.scope.quickHint")}
                <Box sx={{ mt: 1 }}>
                  <Button variant="outlined" onClick={() => openScopeEditor(editing)} disabled={loading}>
                    {t("admin.packages.scope.edit")}
                  </Button>
                </Box>
              </Alert>
            ) : (
              <Alert severity="warning">{t("admin.packages.scope.createFirst")}</Alert>
            )}
          </Stack>
        </Box>
      </Drawer>

      {/* Scope editor (advanced: labs + tags) */}
      <PackScopeEditor
        open={scopeOpen}
        onClose={() => setScopeOpen(false)}
        pack={scopePack}
        onSaved={() => fetchPacks()}
      />

      {/* Delete dialog */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>{t("admin.packages.dialog.deleteTitle")}</DialogTitle>
        <DialogContent>
          <Typography>{t("admin.packages.dialog.deleteText", { code: deleteTarget?.code })}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>{t("common.cancel")}</Button>
          <Button color="error" variant="contained" onClick={doDelete} disabled={loading}>
            {t("common.delete")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
